import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, finalize, takeUntil } from 'rxjs';
import { PermissionDirective, PermissionService, ConfigStateService } from '@abp/ng.core';

// PrimeNG
import { MessageService } from 'primeng/api';
import { EditorModule } from 'primeng/editor';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Tooltip } from 'primeng/tooltip';

// App Components
import { SectionCardComponent } from '../shared.component';
import { SafeHtmlPipe } from '../../../shared/pipe/safe-html.pipe';
import { Note } from '../../../api/common/note';
import { NoteService, GiPConfirmService } from '../../../services';
import { EntityType } from '../../../api/enum';
import { PTDatePipe } from '../../../shared/pipe';

@Component({
  selector: 'app-note-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SkeletonModule,
    SectionCardComponent,
    ButtonModule,
    DialogModule,
    EditorModule,
    SafeHtmlPipe,
    Tooltip,
    PermissionDirective,
    PTDatePipe,
  ],
  templateUrl: 'note-card.component.html',
  styleUrls: ['note-card.component.scss'],
  providers: [NoteService, PermissionService],
})
export class NoteCardComponent implements OnChanges, OnDestroy {
  @Input() title: string = 'Matter Notes';
  @Input() entityType: number | undefined = undefined;
  @Input() entityId: string | undefined = undefined;
  @Input() defaultOpenViewMore: boolean = false;

  destroy$ = new Subject<void>();
  private pendingOpen: boolean = false;
  EntityType = EntityType;

  loading: boolean = true;
  notes: Note[] = [];
  totalNotes: number = 0;

  visible: boolean = false;
  showAddNote: boolean = false;
  isSaving: boolean = false;
  showEditNote: boolean = false;
  newNote: string = '';
  editingNote: Note | null = null;

  allNotes: Note[] = [];
  loadingAll: boolean = false;

  currentUserId: string | undefined;

  constructor(
    private readonly noteService: NoteService,
    private readonly messageService: MessageService,
    private readonly permissionService: PermissionService,
    private readonly config: ConfigStateService,
    private readonly GiPConfirmService: GiPConfirmService
  ) {}

  ngOnInit() {
    this.config.getDeep$('currentUser.id').subscribe((id) => {
      this.currentUserId = id;
    });
  }

  isNoteCreatorOrManager(creatorId: string): boolean {
    return this.currentUserId === creatorId || this.permissionService.getGrantedPolicy('GCM.Note.Manage');
  }

  showDialog() {
    this.visible = true;
    this.loadAllNotes();
  }

  showAddNoteDialog() {
    this.showAddNote = true;
    this.newNote = '';
  }

  saveNote() {
    if (!this.newNote || !this.entityId || this.entityType === undefined || this.isSaving) return;

    this.isSaving = true;

    const note = {
      entityId: this.entityId,
      entityType: this.entityType,
      content: this.newNote,
    } as Partial<Note>;

    this.noteService
      .addNote(note)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSaving = false;
          this.showAddNote = false;
          this.newNote = '';
        })
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Note added successfully',
          });
          this.loadNotes();
        },
        error: (error) => {
          console.error('Failed to add note:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add note',
          });
        },
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.handleDefaultOpenViewMoreChange(changes);
    this.handleEntityChanges(changes);
  }

  private handleDefaultOpenViewMoreChange(changes: SimpleChanges) {
    const defaultOpenViewMoreChange = changes['defaultOpenViewMore'];

    if (!defaultOpenViewMoreChange || defaultOpenViewMoreChange.currentValue === defaultOpenViewMoreChange.previousValue) {
      return;
    }

    this.pendingOpen = defaultOpenViewMoreChange.currentValue;

    if (!this.pendingOpen && this.visible) {
      this.visible = false;
    }
  }

  private handleEntityChanges(changes: SimpleChanges) {
    const entityIdChange = changes['entityId'];
    const entityTypeChange = changes['entityType'];
    const entityChanged =
      (entityIdChange && entityIdChange.currentValue !== entityIdChange.previousValue) ||
      (entityTypeChange && entityTypeChange.currentValue !== entityTypeChange.previousValue);

    if (!this.entityId || this.entityType === undefined) {
      return;
    }

    if (entityChanged) {
      this.loadNotes();
    }

    if (this.pendingOpen) {
      this.showDialog();
      this.pendingOpen = false;
    }
  }

  loadNotes() {
    if (!this.entityId || this.entityType == undefined) return;

    const canViewNote = this.permissionService.getGrantedPolicy('GCM.Note');

    if (!canViewNote) {
      this.notes = [];
      this.totalNotes = 0;
      this.loading = false;
      return;
    }

    this.loading = true;
    this.noteService
      .getNotes(this.entityId, this.entityType, '', 0, 2)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (result) => {
          this.notes = result.items;
          this.totalNotes = result.totalCount ?? 0;
        },
        error: (error) => {
          console.error('Failed to load notes:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load notes',
          });
        },
      });
  }

  loadAllNotes() {
    if (!this.entityId || this.entityType == undefined) return;

    this.loadingAll = true;
    this.noteService
      .getNotes(this.entityId, this.entityType, '', 0, 1000)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loadingAll = false))
      )
      .subscribe({
        next: (result) => {
          this.allNotes = result.items;
          if (result.items.length === 0) {
            this.visible = false;
          }
        },
        error: (error) => {
          console.error('Failed to load all notes:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load all notes',
          });
        },
      });
  }

  onDialogShow() {
    document.body.style.overflow = 'hidden';
  }

  onDialogHide() {
    document.body.style.overflow = 'auto';
  }

  private getContentPreview(content?: string): string {
    if (!content) return 'this note';
    // Remove HTML tags and trim whitespace
    const text = content.replace(/<[^>]*>/g, '').trim();
    // Return first 50 characters with ellipsis if longer
    return text.length > 50 ? text.substring(0, 50) + '...' : text;
  }

  deleteNote(noteId: string) {
    const note = this.allNotes.find(n => n.id === noteId);
    const contentPreview = this.getContentPreview(note?.content || undefined);

    this.GiPConfirmService.confirmDelete({
      resourceType: 'Note',
      itemName: contentPreview,
      onConfirm: () => {
        this.noteService
          .deleteNote(noteId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Note deleted successfully',
              });
              this.loadAllNotes();
              this.loadNotes();
            },
            error: (error) => {
              console.error('Failed to delete note:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete note',
              });
            },
          });
      },
    });
  }

  editNote(note: Note) {
    this.editingNote = note;
    this.newNote = note.content ?? '';
    this.showEditNote = true;
  }

  updateNote() {
    if (!this.newNote || !this.editingNote) return;

    this.isSaving = true;

    const updatedNote: Note = {
      ...this.editingNote,
      content: this.newNote,
    };

    this.noteService
      .updateNote(updatedNote)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.showEditNote = false;
          this.newNote = '';
          this.editingNote = null;
          this.isSaving = false;
        })
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Note updated successfully',
          });
          this.loadAllNotes();
          this.loadNotes();
        },
        error: (error) => {
          console.error('Failed to update note:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update note',
          });
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
